import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import MainLayout from '../../../layouts/MainLayout';
import EmployeeForm from '../../../components/hr/EmployeeForm';
import { Card, Button, Space, message } from 'antd';
import { EditOutlined, ArrowRightOutlined, UserOutlined } from '@ant-design/icons';
import { employeesApi } from '../../../utils/api';
import moment from 'moment-jalaali';
import { formatJalaliApiDate } from '../../../utils/dateUtils';
import { FormSkeleton } from '../../../components/SkeletonLoaders';
import { useDetailReturnRefs } from '../../../hooks/useListReturnState';

const EditEmployee = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { returnToRef, buildGoBackState } = useDetailReturnRefs();
  const goBackToList = () =>
    navigate(returnToRef.current || '/hr/employees', { state: buildGoBackState() });
  const [loading, setLoading] = useState(false);
  const [initialValues, setInitialValues] = useState(null);
  const [fetching, setFetching] = useState(true);
  const [existingImageUrl, setExistingImageUrl] = useState(null);

  useEffect(() => {
    const loadEmployee = async () => {
      setFetching(true);
      try {
        const res = await employeesApi.getById(id);
        const item = res.data || {};
        const detail = item.employee_detail || {};
        setExistingImageUrl(item.image_url || item.image || null);
        
        // تبدیل تاریخ‌ها از ISO string به Date object برای PersianDatePicker
        const parseDate = (dateString) => {
          if (!dateString) return null;
          try {
            // اگر تاریخ به صورت ISO string است (مثلاً: "2025-07-23T00:00:00+03:30")
            if (typeof dateString === 'string') {
              const dateOnly = dateString.split('T')[0]; // فقط بخش تاریخ را بگیر
              const m = moment(dateOnly, 'YYYY-MM-DD');
              if (m.isValid()) {
                return m.toDate();
        }
              return new Date(dateOnly);
            }
            return new Date(dateString);
          } catch (e) {
            return null;
          }
        };

        // تبدیل login و email_notifications
        const loginValue = item.login === 'enable' || item.login === 'yes' ? 'yes' : 'no';
        const emailNotificationsValue = item.email_notifications === 1 || item.email_notifications === 'yes' ? 'yes' : 'no';

        // استخراج آی‌دی reporting_to (ممکن است آبجکت یا عدد باشد)
        const reportingToId = typeof detail.reporting_to === 'object' && detail.reporting_to !== null
          ? detail.reporting_to.id
          : (detail.reporting_to || null);

        // استخراج آی‌دی دپارتمان و سمت شغلی
        const departmentId = detail.department?.id || detail.department_id || null;
        const designationId = detail.designation?.id || detail.designation_id || null;

        // ساخت initialValues — department/designation/joining به صورت تخت (هم‌تراز با Form.Item)
        const formValues = {
          employee_id: detail.employee_id || item.employee_id || null,
          name: item.name || '',
          email: item.email || '',
          mobile: item.mobile || '',
          status: item.status || 'active',
          date_of_birth: parseDate(detail.date_of_birth),
          gender: item.gender || 'male',
          national_id: item.national_id || null,
          father_name: item.father_name || null,
          insurance_code: item.insurance_code || null,
          reporting_to: reportingToId,
          locale: item.locale || 'fa',
          login: loginValue,
          email_notifications: emailNotificationsValue,
          address: detail.address || null,
          about_me: detail.about_me || null,
          hourly_rate: detail.hourly_rate || null,
          probation_end_date: parseDate(detail.probation_end_date),
          notice_period_start_date: parseDate(detail.notice_period_start_date),
          notice_period_end_date: parseDate(detail.notice_period_end_date),
          employment_type: detail.employment_type || null,
          internship_end_date: parseDate(detail.internship_end_date),
          contract_end_date: parseDate(detail.contract_end_date),
          marital_status: detail.marital_status || null,
          company_address: detail.company_address_id || null,
          department_id: departmentId,
          designation_id: designationId,
          joining_date: parseDate(detail.joining_date),
        };

        setInitialValues(formValues);
      } catch (error) {
        console.error('خطا در دریافت کارمند:', error);
        message.error('خطا در دریافت اطلاعات کارمند');
      } finally {
        setFetching(false);
      }
    };
    
    if (id) {
      loadEmployee();
    }
  }, [id]);

  const resolveId = (...candidates) => {
    for (const raw of candidates) {
      if (raw == null || raw === '') continue;
      if (typeof raw === 'object') {
        const nested = raw.id ?? raw.value;
        if (nested != null && nested !== '') return nested;
        continue;
      }
      return raw;
    }
    return null;
  };

  const toNumberOrNull = (value) => {
    if (value == null || value === '') return null;
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
  };

  const appendValue = (formData, key, value) => {
    if (value == null || value === '') return;
    formData.append(key, String(value));
  };

  const appendNested = (formData, prefix, value) => {
    if (value == null) return;
    if (Array.isArray(value)) {
      value.forEach((item, index) => appendNested(formData, `${prefix}[${index}]`, item));
      return;
    }
    if (typeof value === 'object' && !(value instanceof Date) && !(value instanceof File)) {
      Object.entries(value).forEach(([k, v]) => appendNested(formData, `${prefix}[${k}]`, v));
      return;
    }
    formData.append(prefix, String(value));
  };

  /** پیلود مطابق قرارداد Postman بک‌اند */
  const buildPayload = (values, fileOptions = {}) => {
    const departmentId = toNumberOrNull(
      resolveId(
        values.department_id,
        values.employee_detail?.department?.id,
        values.employee_detail?.department,
        values.department?.id,
        values.department,
      ),
    );
    const designationId = toNumberOrNull(
      resolveId(
        values.designation_id,
        values.employee_detail?.designation?.id,
        values.employee_detail?.designation,
        values.designation?.id,
        values.designation,
      ),
    );

    const payload = {
      name: values.name || '',
      email: values.email || '',
      mobile: values.mobile || '',
      date_of_birth: formatJalaliApiDate(values.date_of_birth),
      gender: values.gender || null,
      national_id: values.national_id || null,
      father_name: values.father_name || null,
      insurance_code: values.insurance_code || null,
      reporting_to: toNumberOrNull(values.reporting_to),
      locale: values.locale || 'fa',
      address: values.address || null,
      about_me: values.about_me || null,
      login: values.login || 'yes',
      email_notifications: values.email_notifications || 'yes',
      hourly_rate: toNumberOrNull(values.hourly_rate),
      tags: values.tags || null,
      probation_end_date: formatJalaliApiDate(values.probation_end_date),
      notice_period_start_date: formatJalaliApiDate(values.notice_period_start_date),
      notice_period_end_date: formatJalaliApiDate(values.notice_period_end_date),
      employment_type: values.employment_type || null,
      internship_end_date: formatJalaliApiDate(values.internship_end_date),
      contract_end_date: formatJalaliApiDate(values.contract_end_date),
      marital_status: values.marital_status || null,
      company_address: values.company_address || null,
      marriage_anniversary_date: formatJalaliApiDate(values.marriage_anniversary_date),
      status: values.status || 'active',
      employee_detail: {
        employee_id: values.employee_id || null,
        department: departmentId != null ? { id: departmentId } : null,
        designation: designationId != null ? { id: designationId } : null,
        joining_date: formatJalaliApiDate(values.joining_date),
      },
      image: '',
    };

    if (values.password) {
      payload.password = values.password;
    }

    // حذف عکس، یا جایگزینی با عکس جدید
    if (fileOptions.deleteImage || fileOptions.imageFile) {
      payload.image_delete = 'yes';
    }

    return payload;
  };

  const buildEmployeeFormData = (payload, fileOptions = {}) => {
    const formData = new FormData();
    Object.entries(payload).forEach(([key, value]) => {
      if (key === 'image') return;
      if (key === 'employee_detail' && value && typeof value === 'object') {
        appendNested(formData, 'employee_detail', value);
        return;
      }
      appendValue(formData, key, value);
    });

    if (fileOptions.imageFile) {
      formData.append('image', fileOptions.imageFile);
    } else {
      formData.append('image', '');
    }

    return formData;
  };

  const handleSave = async (values, fileOptions = {}) => {
    setLoading(true);
    try {
      const payload = buildPayload(values, fileOptions);
      // هر دو حالت (با/بدون عکس): FormData + POST + _method=PUT
      await employeesApi.update(id, buildEmployeeFormData(payload, fileOptions));

      message.success('کارمند با موفقیت ویرایش شد');
      goBackToList();
    } catch (error) {
      console.error('خطا در ویرایش:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  if (fetching || !initialValues) {
    return (
      <MainLayout>
        <div className="p-6">
          <Card className="shadow-xl border-0" bodyStyle={{ padding: 0 }}>
            <div className="flex items-center justify-between flex-wrap gap-4 p-6 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-t-xl">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-orange-50 dark:bg-orange-900/20 flex items-center justify-center">
                  <EditOutlined className="text-orange-600 dark:text-orange-400 text-xl" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100 m-0">ویرایش کارمند</h1>
                  <p className="text-gray-500 dark:text-gray-400 text-sm m-0">در حال بارگذاری...</p>
                </div>
              </div>
            </div>
            <div className="p-6 bg-gray-50 dark:bg-gray-900/50 rounded-b-xl">
              <FormSkeleton />
            </div>
          </Card>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="" style={{ fontFamily: 'CustomFont' }}>
        <Card className="shadow-xl border-0" bodyStyle={{ padding: 0 }}>
          <div className="flex items-center justify-between flex-wrap gap-4 p-6 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-t-xl">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-orange-50 dark:bg-orange-900/20 flex items-center justify-center">
                <EditOutlined className="text-orange-600 dark:text-orange-400 text-xl" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100 m-0">ویرایش کارمند</h1>
                <p className="text-gray-500 dark:text-gray-400 text-sm m-0">ویرایش اطلاعات کارمند با شناسه #{id}</p>
              </div>
            </div>
            <Space>
              <Button icon={<ArrowRightOutlined />} onClick={goBackToList} style={{ fontFamily: 'CustomFont' }}>
                بازگشت
              </Button>
              <Button icon={<UserOutlined />} disabled>
                پروفایل
              </Button>
            </Space>
          </div>

          <div className="p-6 bg-gray-50 dark:bg-gray-900/50 rounded-b-xl">
            <EmployeeForm
              initialValues={initialValues}
              onSave={handleSave}
              onCancel={goBackToList}
              loading={loading}
              isEdit={true}
              existingImageUrl={existingImageUrl}
            />
          </div>
        </Card>
      </div>
    </MainLayout>
  );
};

export default EditEmployee;
